/**
 * Enhanced production logger with sanitization and environment awareness
 * Consolidates all logging functionality into a single, robust system
 */

import { getLoggingConfig } from './production-config';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Sanitize data before logging to prevent sensitive information exposure
 */
function sanitizeForLogging(data: any): any {
  const config = getLoggingConfig();
  if (!config.sanitizeOutput) return data;

  if (typeof data === 'string') {
    return data
      .replace(/(api[_-]?key|token|password|secret)[:\s=][^\s&]*/gi, '$1=***')
      .replace(/auth[:\s=][^\s&]*/gi, 'auth=***');
  }
  
  if (typeof data === 'object' && data !== null) {
    const sanitized: any = Array.isArray(data) ? [] : {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes('key') || lowerKey.includes('token') || 
          lowerKey.includes('password') || lowerKey.includes('secret') ||
          lowerKey.includes('auth')) {
        sanitized[key] = '***';
      } else {
        sanitized[key] = sanitizeForLogging(value);
      }
    }
    return sanitized;
  }
  
  return data;
}

/**
 * Production-aware logging class that respects environment settings
 * and provides specialized logging methods for common use cases.
 */
class ProductionLogger {
  private config = getLoggingConfig();

  /**
   * Log debug information (development only)
   */
  debug(message: string, ...args: unknown[]) {
    if (this.config.enableDebug) {
      const sanitizedArgs = args.map(sanitizeForLogging);
      console.log(`[DEBUG] ${message}`, ...sanitizedArgs);
    }
  }

  /**
   * Log informational messages (development only)
   */
  info(message: string, ...args: unknown[]) {
    if (this.config.enableInfo) {
      const sanitizedArgs = args.map(sanitizeForLogging);
      console.info(`[INFO] ${message}`, ...sanitizedArgs);
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, ...args: unknown[]) {
    if (this.config.enableWarn) {
      const sanitizedArgs = args.map(sanitizeForLogging);
      console.warn(`[WARN] ${message}`, ...sanitizedArgs);
    }
  }

  /**
   * Log error messages (always logged for monitoring)
   */
  error(message: string, ...args: unknown[]) {
    if (this.config.enableError) {
      const sanitizedArgs = args.map(sanitizeForLogging);
      console.error(`[ERROR] ${message}`, ...sanitizedArgs);
    }
  }

  /**
   * Log collection filtering debug information
   */
  filteringDebug(collectionName: string, totalProducts: number, filteredCount: number) {
    this.debug(`${collectionName} Collection Filtering`, {
      totalProducts,
      filteredCount,
      filterRatio: `${Math.round((filteredCount / totalProducts) * 100)}%`
    });
  }

  /**
   * Log performance timing information (development only)
   */
  performanceLog(label: string, startTime: number) {
    if (this.config.enableDebug) {
      const duration = performance.now() - startTime;
      this.debug(`Performance: ${label}`, `${duration.toFixed(2)}ms`);
    }
  }

  /**
   * Performance logging wrapper (development only)
   */
  performance<T>(label: string, fn: () => T): T {
    if (!this.config.enableDebug) {
      return fn();
    }
    
    const startTime = performance.now();
    const result = fn();
    this.performanceLog(label, startTime);
    return result;
  }

  /**
   * Conditional logging
   */
  conditional(condition: boolean, level: LogLevel, message: string, ...args: unknown[]) {
    if (condition) {
      this[level](message, ...args);
    }
  }
}

/**
 * Singleton instance of ProductionLogger for use throughout the application
 * Provides a unified, production-safe logging interface with sanitization
 */
export const productionLogger = new ProductionLogger();

// Legacy exports for backward compatibility during migration
export const logger = productionLogger;
export const devLog = (message: string, ...args: unknown[]) => productionLogger.debug(message, ...args);
export const devWarn = (message: string, ...args: unknown[]) => productionLogger.warn(message, ...args);
export const devError = (message: string, ...args: unknown[]) => productionLogger.error(message, ...args);
export const devInfo = (message: string, ...args: unknown[]) => productionLogger.info(message, ...args);